import type { ReactNode } from "react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Menu } from "lucide-react";
import { IconButton } from "../primitives/IconButton";
import { Dropdown } from "../primitives/Dropdown";
import { cn } from "../utils/cn";
import { CollapsibleSidebar } from "./CollapsibleSidebar";
import { useWorkspaceSidebarState } from "./useWorkspaceSidebarState";
import { APP_VERSION } from "../../version";
import { OrgSwitcher } from "../components/ops/OrgSwitcher";

type AppShellSidebar = ReactNode | ((options: { isExpanded: boolean }) => ReactNode);

export function AppShell({
  children,
  sidebar,
  topbar,
  modules = [],
  userName = "Utilisateur",
  userMenuItems = [],
  logoText = "XLFlow",
  onLogoClick,
  sidebarTitle,
  sidebarPinnedWidth,
  containerFluid = false,
}: {
  topbar?: ReactNode;
  children: ReactNode;
  sidebar?: AppShellSidebar;
  modules?: { label: string; onClick?: () => void; separator?: boolean; sectionHeader?: boolean }[];
  userName?: string;
  userMenuItems?: { label: string; onClick?: () => void }[];
  logoText?: string;
  onLogoClick?: () => void;
  /** Sidebar title (e.g. "Inventaire" instead of "Navigation") */
  sidebarTitle?: string;
  /** Sidebar width when pinned (e.g. 220 for narrower menu) */
  sidebarPinnedWidth?: number;
  /** Use full-width container (ds-container-fluid) for workspace modules (catalog, inventory, orders, ops) */
  containerFluid?: boolean;
}) {
  const defaultUserMenuItems = [
    {
      label: "Profil",
      onClick: () => {
        window.location.hash = "#/profile";
      },
    },
    {
      label: "Paramètres",
      onClick: () => {
        window.location.hash = "#/settings";
      },
    },
    {
      label: "Déconnexion",
      onClick: () => {
        window.dispatchEvent(new CustomEvent("xlflow:user-menu", { detail: { action: "logout" } }));
      },
    },
  ];
  const resolvedUserMenuItems = userMenuItems.length > 0 ? userMenuItems : defaultUserMenuItems;

  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuId = useId();
  const userTriggerRef = useRef<HTMLButtonElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const [userMenuPos, setUserMenuPos] = useState<{ top: number; left: number; placement: "down" | "up" }>({
    top: 0,
    left: 0,
    placement: "down",
  });
  const sidebarState = useWorkspaceSidebarState({
    collapsedKey: "xlflow.sidebarCollapsed",
    pinnedKey: "xlflow.sidebarPinned",
    defaultCollapsed: false,
    defaultPinned: true,
  });

  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const update = () => {
      setIsMobile(media.matches);
      if (media.matches) {
        setMobileOpen(false);
      }
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const hasSidebar = Boolean(sidebar);
  const initials = userName
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const handleLogoClick =
    onLogoClick ??
    (() => {
      window.location.hash = "#/";
    });

  useEffect(() => {
    if (!userMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setUserMenuOpen(false);
      }
    };
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (userMenuRef.current?.contains(target) || userTriggerRef.current?.contains(target)) {
        return;
      }
      setUserMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [userMenuOpen]);

  useLayoutEffect(() => {
    if (!userMenuOpen) return;
    const trigger = userTriggerRef.current;
    const menu = userMenuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp = spaceBelow < menuRect.height && spaceAbove > menuRect.height;
    const top = shouldOpenUp ? rect.top - menuRect.height - 6 : rect.bottom + 6;
    const left = Math.max(8, Math.min(rect.right - menuRect.width, window.innerWidth - menuRect.width - 8));
    setUserMenuPos({ top, left, placement: shouldOpenUp ? "up" : "down" });
  }, [userMenuOpen]);

  return (
    <div className="flex min-h-0 flex-1 flex-col w-full bg-bg">
      <div className="fixed top-0 left-0 right-0 z-10 border-b border-border bg-surface">
        <div className="ds-container-fluid h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {isMobile ? (
              <IconButton
                icon={<Menu size={16} strokeWidth={1.5} />}
                ariaLabel="Ouvrir le menu"
                intent="ghost"
                size="sm"
                onClick={() => setMobileOpen(true)}
              />
            ) : modules.length ? (
              <Dropdown label="≡" items={modules} />
            ) : null}
            <button
              type="button"
              onClick={handleLogoClick}
              className="text-lg font-semibold text-primary tracking-tight"
              aria-label="Retour au tableau de bord"
            >
              {logoText}
            </button>
          </div>
          <div className="flex-1">{topbar}</div>
          <div className="flex items-center gap-3">
            <OrgSwitcher />
            <span className="text-xs text-muted hidden sm:inline" title="Version de l'application — Rafraîchir (Ctrl+Shift+R) si vous ne voyez pas la dernière version">
              v{APP_VERSION}
            </span>
            <button
              ref={userTriggerRef}
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-surface text-xs font-semibold text-text shadow-sm"
              onClick={() => setUserMenuOpen((prev) => !prev)}
              aria-label="Menu utilisateur"
            >
              {initials}
            </button>
            {userMenuOpen
              ? createPortal(
                  <div
                    ref={userMenuRef}
                    className="dropdown-panel fixed z-50 w-44 rounded-md border border-border bg-surface shadow-sm"
                    style={{ top: userMenuPos.top, left: userMenuPos.left }}
                    data-placement={userMenuPos.placement}
                  >
                    {resolvedUserMenuItems.map((item) => (
                      <button
                        key={`${userMenuId}-${item.label}`}
                        type="button"
                        className={cn("block w-full px-3 py-2 text-left text-sm text-text hover:bg-surface-muted")}
                        onClick={() => {
                          item.onClick?.();
                          setUserMenuOpen(false);
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                    <div className="border-t border-border px-3 py-2">
                      <div className="text-xs text-muted">Version {APP_VERSION}</div>
                    </div>
                  </div>,
                  document.body
                )
              : null}
          </div>
        </div>
      </div>
      <div
        className={cn(
          "grid min-h-0 flex-1 gap-6 pt-20 w-full",
          containerFluid ? "ds-container-fluid" : "ds-container"
        )}
        style={{ gridTemplateColumns: !hasSidebar || isMobile ? "1fr" : "auto 1fr" }}
      >
        {!isMobile && hasSidebar ? (
          <CollapsibleSidebar
            pinned={sidebarState.pinned}
            collapsed={sidebarState.collapsed}
            onToggleCollapsed={sidebarState.toggleCollapsed}
            onTogglePinned={sidebarState.togglePinned}
            allowHoverOpen
            title={sidebarTitle}
            pinnedWidth={sidebarPinnedWidth}
          >
            {sidebar}
          </CollapsibleSidebar>
        ) : null}
        <main className="min-h-0 min-w-0 flex-1 flex flex-col overflow-hidden">{children}</main>
      </div>
      {hasSidebar ? (
        <CollapsibleSidebar
          isMobile
          isOpen={mobileOpen}
          onOpenChange={setMobileOpen}
          pinned={sidebarState.pinned}
          collapsed={sidebarState.collapsed}
          onToggleCollapsed={sidebarState.toggleCollapsed}
          onTogglePinned={sidebarState.togglePinned}
          title={sidebarTitle}
        >
          {sidebar}
        </CollapsibleSidebar>
      ) : null}
    </div>
  );
}
